import React from "react";
import { Link, useLocation } from "react-router-dom";
import { AccountIcon, SubscriptionIcon, PreferencesIcon, NotificationsIcon, ArrowLeftIcon } from "../../../design-system";
import { typography, surfaces, borders, textColors } from "../../../design-system/tokens";

const SettingsSidebar = ({ username, isOpen, toggleSidebar }) => {
  const location = useLocation();
  
  const settingsItems = [
    {
      id: "account",
      name: "Account",
      icon: <AccountIcon size="md" />,
      path: `/settings/${username}/account`
    },
    {
      id: "subscription",
      name: "Subscription",
      icon: <SubscriptionIcon size="md" />,
      path: `/settings/${username}/subscription`
    },
    {
      id: "preferences",
      name: "Preferences",
      icon: <PreferencesIcon size="md" />,
      path: `/settings/${username}/preferences`
    },
    {
      id: "notifications",
      name: "Notifications",
      icon: <NotificationsIcon size="md" />,
      path: `/settings/${username}/notifications`
    }
  ];

  const isActive = (path) => location.pathname === path;

  // Active state: keep brand blue in light mode, but use a neutral (non-blue) highlight in dark mode
  const activeLinkClasses =
    "bg-blue-50 text-blue-700 border-r-2 border-blue-600 dark:bg-dark-800 dark:text-dark-100 dark:border-dark-600";
  const inactiveLinkClasses = `${textColors.secondary} ${surfaces.hover}`;

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black dark:bg-dark-950 bg-opacity-50 dark:bg-opacity-70 z-20 lg:hidden"
          onClick={toggleSidebar}
        />
      )}
      
      {/* Sidebar */}
      <div 
        className={`
          fixed lg:static inset-y-0 left-0 z-30 lg:z-0
          w-64
          transform ${isOpen ? 'translate-x-0' : '-translate-x-full'} 
          lg:translate-x-0 transition-transform duration-300 ease-in-out
          flex flex-col
          ${surfaces.glass} border-r ${borders.glass}
        `}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-4 lg:hidden border-b ${borders.glass}`}>
          <h2 className={`${typography.h5} ${textColors.primary}`}>Settings</h2>
          <button
            onClick={toggleSidebar}
            className={`p-2 rounded-md transition-colors ${textColors.secondary} ${surfaces.hover}`}
            aria-label="Close settings menu"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-2">
          <div className="hidden lg:block mb-6">
            <h2 className={`${typography.h5} ${textColors.primary}`}>Settings</h2>
          </div>
          
          {settingsItems.map((item) => (
            <Link
              key={item.id}
              to={item.path}
              className={`flex items-center px-3 py-2 rounded-md ${typography.bodySmall} font-medium transition-colors ${
                isActive(item.path) ? activeLinkClasses : inactiveLinkClasses
              }`}
              onClick={() => {
                // Close sidebar on mobile when item is clicked
                if (window.innerWidth < 1024) {
                  toggleSidebar();
                }
              }}
            >
              <span className="mr-3">{item.icon}</span>
              {item.name}
            </Link>
          ))}
        </nav>

        {/* Footer */}
        <div className={`p-4 border-t ${borders.glass}`}>
          <Link
            to={`/edit/${username}`}
            className={`flex items-center ${typography.bodySmall} transition-colors ${textColors.secondary} hover:text-gray-900 dark:hover:text-dark-200`}
          >
            <ArrowLeftIcon size="sm" className="mr-2" />
            Back to Dashboard
          </Link>
        </div>
      </div>
    </>
  );
};

export default SettingsSidebar; 